import { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { placesService } from '@/services/places.service';

export const GooglePlacesAutocomplete = ({
  value,
  onChange,
  placeholder = "Search for a location...",
  types = [],
  className,
  disabled = false
}) => {
  const [input, setInput] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    // Set initial value if provided or when value changes
    if (value && value.name) {
      setInput(value.name);
      setSelectedPlace(value);
    } else if (!value) {
      // Clear input if value is explicitly null
      setInput('');
      setSelectedPlace(null);
    }
  }, [value]);

  useEffect(() => {
    // Handle clicks outside to close dropdown
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Debounced search
    const timer = setTimeout(async () => {
      if (input.length >= 3 && !selectedPlace) {
        setLoading(true);
        try {
          const results = await placesService.autocomplete(input, types);
          setPredictions(results);
          setShowDropdown(results.length > 0);
        } catch (error) {
          console.error('GooglePlacesAutocomplete: Autocomplete error:', error);
          setPredictions([]);
          setShowDropdown(false);
        } finally {
          setLoading(false);
        }
      } else if (input.length < 3) {
        setPredictions([]);
        setShowDropdown(false);
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [input, types, selectedPlace]);

  const handleSelectPlace = async (prediction) => {
    setLoading(true);
    
    try {
      const details = await placesService.getDetails(prediction.placeId);
      setInput(details.name || '');
      setSelectedPlace(details);
      setShowDropdown(false);
      setPredictions([]);
      if (onChange) {
        onChange(details);
      }
    } catch (error) {
      console.error('Failed to get place details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setInput('');
    setSelectedPlace(null);
    setPredictions([]);
    setShowDropdown(false);
    setLoading(false);
    
    if (onChange) {
      onChange(null);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInput(newValue);
    
    // If user clears or modifies selected place, reset selection
    if (selectedPlace) {
      setSelectedPlace(null);
      if (onChange) {
        onChange(null);
      }
    }
  };

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={input}
          onChange={handleInputChange}
          placeholder={placeholder}
          title={input}
          className="pl-10 pr-10 truncate"
          disabled={disabled}
          onFocus={() => {
            if (predictions.length > 0) {
              setShowDropdown(true);
            }
          }}
        />
        {input && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        {loading && (
          <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div 
          className="absolute z-[9999] w-full mt-1 bg-background border rounded-lg shadow-lg max-h-60 overflow-y-auto"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#d1d5db transparent'
          }}
        >
          <style jsx>{`
            div::-webkit-scrollbar {
              width: 6px;
            }
            div::-webkit-scrollbar-track {
              background: transparent;
            }
            div::-webkit-scrollbar-thumb {
              background-color: #d1d5db;
              border-radius: 3px;
            }
            div::-webkit-scrollbar-thumb:hover {
              background-color: #9ca3af;
            }
          `}</style>
          {predictions.map((prediction) => (
            <button
              key={prediction.placeId}
              type="button"
              className="w-full text-left px-4 py-3 hover:bg-muted transition-colors border-b last:border-b-0"
              onClick={() => handleSelectPlace(prediction)}
            >
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{prediction.mainText}</div>
                  {prediction.secondaryText && (
                    <div className="text-xs text-muted-foreground truncate">
                      {prediction.secondaryText}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Show selected place info - compact version */}
      {selectedPlace && (
        <div className="mt-1.5 text-xs text-muted-foreground">
          <div className="truncate">{selectedPlace.address}</div>
        </div>
      )}
      
      {/* Show loading message if Google Maps not ready */}
      {!isScriptLoaded && input.length >= 3 && (
        <div className="mt-1.5 text-xs text-muted-foreground">
          Loading Google Maps...
        </div>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
